import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { validateToken } from "../tokenValidation";
import { FaArrowLeft } from "react-icons/fa";
import secureStorage from "../utils/secureStorage";
Modal.setAppElement("#root");

const PatientProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [abhaAddress] = useState(location.state?.abhaAddress);
  const [patientData, setPatientData] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [abhaCard, setAbhaCard] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Validate token on component mount
  useEffect(() => {
    validateToken(navigate);
  }, [navigate]);

  // Fetch patient profile and QR code on abhaNumber change
  useEffect(() => {
    if (abhaAddress) {
      fetchPatientProfile();
      fetchQrCode();
    }
  }, [abhaAddress]);

  // Fetch patient profile
  const fetchPatientProfile = async () => {
    setIsLoading(true);
    try {
      const clinicId = secureStorage.get("clinic");
      const response = await fetch(
        `${API_URL}abdm-profile/get-profile?abhaAddress=${abhaAddress}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-HIP-ID":clinicId,
            Authorization: `Bearer ${secureStorage.get("authToken")}`,
          },
        }
      );

      const jsonResponse = await response.json();

      if (response.ok && jsonResponse.status === 200 && jsonResponse.data) {
        setPatientData(jsonResponse.data);
        // console.log(patientData, "PatientData")
      } else {
        console.error("Failed to fetch patient profile:", jsonResponse.message);
        toast.error("Failed to fetch patient profile");
        navigate("/viewall");
      }
    } catch (error) {
      console.error("Error fetching patient profile:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch QR code
  const fetchQrCode = async () => {
    setIsLoading(true);
    try {
      const clinicId = secureStorage.get("clinic");
      const response = await fetch(
        `${API_URL}abdm-profile/generateQrCode?abhaAddress=${abhaAddress}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-HIP-ID":clinicId,
            Authorization: `Bearer ${secureStorage.get("authToken")}`,
          },
        }
      );

      const jsonResponse = await response.json();

      if (response.ok && jsonResponse.status === 200 && jsonResponse.data) {

        console.log("base64 length:", jsonResponse.data?.length);
        console.log("base64 prefix:", jsonResponse.data?.slice(0, 20));
        const cleanBase64 = jsonResponse.data.replace(/\s/g, "");
        const mimeType = cleanBase64.startsWith("/9j/") ? "image/jpeg" : "image/png";
        setQrCode(`data:${mimeType};base64,${cleanBase64}`);
      } else {
        console.error("Failed to fetch QR Code:", jsonResponse.message);
      }
    } catch (error) {
      console.error("Error fetching QR Code:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch ABHA card
  const fetchAbhaCard = async () => {
    setIsLoading(true);
    try {
      const clinicId = secureStorage.get("clinic");
      const response = await fetch(
        `${API_URL}abdm-profile/abha-card?abhaAddress=${abhaAddress}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-HIP-ID":clinicId,
            Authorization: `Bearer ${secureStorage.get("authToken")}`,
          },
        }
      );

      const jsonResponse = await response.json();

      if (response.ok && jsonResponse.status === 200 && jsonResponse.data) {
        setAbhaCard(`data:image/png;base64,${jsonResponse.data}`);
        setIsModalOpen(true);
      } else {
        console.error("ABHA card fetch failed:", jsonResponse.message);
        toast.error("Failed to fetch ABHA card");
      }
    } catch (error) {
      console.error("Error fetching ABHA card:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle viewing ABHA card
  const handleViewAbhaCard = () => {
    fetchAbhaCard();
  };

  return (
    <>
      <div className="flex items-center justify-between m-6 relative">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-0 flex items-center text-teal-700 hover:text-teal-900 font-medium"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>

        <h1 className="w-full text-center text-3xl font-bold text-gray-800">Profile Details</h1>
      </div>

      <div className="bg-gray-100 min-h-screen flex justify-center items-center py-8 px-4">
        {/* Loading Spinner */}
        {isLoading && (
          <div
            role="status"
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          >
            <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="sr-only">Loading...</span>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-lg w-full max-w-4xl overflow-hidden">
          {/* Header Section */}
          <div className="relative bg-teal-600 h-28 flex items-center px-6">
            <button
              onClick={() => navigate("/hospital-dashboard")}
              className="bg-white text-teal-600 px-4 py-2 rounded-md font-semibold hover:bg-gray-100"
            >
              Home
            </button>
          </div>

          {/* Profile Section */}
          <div className="px-6 mt-4 flex items-center">
            <div className="w-32 h-32 rounded-full bg-gray-300 overflow-hidden border-4 border-white shadow-md flex items-center justify-center">
              {isLoading ? (
                <div className="w-8 h-8 border-2 border-t-teal-600 border-gray-300 rounded-full animate-spin"></div>
              ) : patientData?.profilePhoto ? (
                <img
                  src={`data:${patientData.profilePhoto.startsWith("/9j/") ? "image/jpeg" : "image/png"};base64,${patientData.profilePhoto.replace(/\s/g, "")}`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    const parent = e.target.parentNode;
                    parent.innerHTML = `<div class='text-gray-500 text-sm'>No Photo</div>`;
                  }}
                />
              ) : (
                <div className="text-gray-500 text-sm">No Photo</div>
              )}
            </div>

            <h3 className="ml-6 text-2xl font-semibold text-teal-700">
              {patientData
                ? `${patientData.firstName} ${patientData.middleName || ""} ${patientData.lastName || ""}`
                : "Loading..."}
            </h3>
          </div>

          {/* Patient Details */}
          <div className="px-6 mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg shadow">
              <h4 className="text-teal-600 font-semibold mb-4">Patient Details</h4>
              <ul className="space-y-2">
                <li>
                  <strong>Name:</strong> {patientData?.firstName} {patientData?.middleName} {patientData?.lastName}
                </li>
                <li>
                  <strong>ABHA Number:</strong> {patientData?.ABHANumber || "N/A"}
                </li>
                <li>
                  <strong>ABHA Address:</strong> {patientData?.preferredAbhaAddress || "N/A"}
                </li>
                <li>
                  <strong>Gender:</strong> {patientData?.gender === "F" ? "Female" : patientData?.gender === "M" ? "Male" : "N/A"}
                </li>
                <li>
                  <strong>DOB:</strong>{" "}
                  {patientData?.yearOfBirth && patientData?.monthOfBirth && patientData?.dayOfBirth
                    ? `${patientData.dayOfBirth}-${patientData.monthOfBirth}-${patientData.yearOfBirth}`
                    : "N/A"}
                </li>
                <li>
                  <strong>Mobile:</strong> {patientData?.mobile || "N/A"}
                </li>
                <li>
                  <strong>Address:</strong> {patientData?.address || "N/A"}
                </li>
                <li>
                  <strong>Pincode:</strong> {patientData?.pincode || "N/A"}
                </li>
              </ul>
            </div>


            <div className="bg-gray-50 p-4 rounded-lg shadow flex flex-col items-center">
              <h4 className="text-teal-600 font-semibold mb-4">QR Code</h4>

              {isLoading ? (
                <div className="w-8 h-8 border-2 border-t-teal-600 border-gray-300 rounded-full animate-spin"></div>
              ) : qrCode ? (
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="w-32 h-32 object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                    const parent = e.target.parentNode;
                    parent.innerHTML = `<div class='text-gray-500 text-sm'>No QR Code</div>`;
                  }}
                />
              ) : (
                <div className="w-32 h-32 bg-gray-100 flex items-center justify-center text-gray-400">
                  No QR Code
                </div>
              )}

              {qrCode && !isLoading && (
                <button
                  onClick={() => saveAs(qrCode, "QRCode.png")}
                  className="mt-4 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                >
                  Download QR Code
                </button>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 mt-6 mb-6 text-center">
            <button
              onClick={handleViewAbhaCard}
              className="bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600"
            >
              View/Download ABHA Card
            </button>
          </div>

          {/* ABHA Card Modal */}
          <Modal
            isOpen={isModalOpen}
            onRequestClose={() => setIsModalOpen(false)}
            className="bg-white max-w-lg mx-auto p-6 rounded-lg shadow-lg relative"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          >
            <h2 className="text-teal-600 text-xl font-semibold mb-4">ABHA Card</h2>
            {abhaCard ? (
              <div className="flex flex-col items-center">
                <img src={abhaCard} alt="ABHA Card" className="w-full h-auto rounded-md mb-4" />
                <button
                  onClick={() => saveAs(abhaCard, "ABHA_Card.png")}
                  className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                >
                  Download ABHA Card
                </button>
              </div>
            ) : (
              <p className="text-gray-500">Loading ABHA Card...</p>
            )}
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default PatientProfile;